/**
 * The profile's fields as a person types them (plan.md step 68): one rule for
 * the completion step and the account page.
 */
import { COUNTRY_CODES } from '@myrave/shared';

/** A network a social link can be on — `GET /social-networks`. */
export interface SocialNetwork { slug: string; label: string; urlPrefix: string | null }

/**
 * The networks to offer until the platform's list answers (or when it cannot)
 * — the completion step REQUIRES a link, so it must never show no box at all.
 */
export const FALLBACK_NETWORKS: SocialNetwork[] = [
  { slug: 'instagram', label: 'Instagram', urlPrefix: 'https://instagram.com/' },
  { slug: 'facebook', label: 'Facebook', urlPrefix: 'https://facebook.com/' },
  { slug: 'tiktok', label: 'TikTok', urlPrefix: 'https://tiktok.com/@' },
  { slug: 'soundcloud', label: 'SoundCloud', urlPrefix: 'https://soundcloud.com/' },
];

/**
 * A social link as typed → a web address, or null when it cannot be one:
 * - a full `https://…` is kept;
 * - "instagram.com/dana" (an address without its scheme) gets `https://`;
 * - a handle ("@dana" or "dana") goes after the network's own address.
 * The API refuses anything but a web address (`PATCH /me/profile`).
 */
export function socialLink(typed: string, urlPrefix: string | null): string | null {
  const v = typed.trim();
  if (!v) return null;
  if (/^https?:\/\/\S+$/i.test(v)) return v;
  if (/^[\w-]+(\.[\w-]+)+\/\S*$/.test(v)) return `https://${v}`;
  const handle = v.replace(/^@+/, '');
  if (!urlPrefix || !/^[\w.-]+$/.test(handle)) return null;
  return `${urlPrefix}${handle}`;
}

/** A country's name in the reader's language, from the runtime — no list of names to keep. */
export function countryLabel(code: string, locale: string): string {
  try { return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code; } catch { return code; }
}

/** Every country, named in the reader's language and in its alphabetical order. */
export function countryOptions(locale: string): { code: string; name: string }[] {
  return COUNTRY_CODES.map((code) => ({ code, name: countryLabel(code, locale) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}
